import pointInPolygon from 'point-in-polygon';
import * as THREE from 'three';
import { Quaternion, Raycaster, Vector2, Vector3 } from 'three';

import { isValidNumber } from '../../utils/math/number';

export type Point3d = { x: number; y: number; z: number };
export type Point2d = { x: number; y: number };

export type Coordinates = {
  /**
   * Northing (latitude for WGS 84)
   */
  northing: number;
  /**
   * Easting (longitude for WGS 84)
   */
  easting: number;
  height: number;
};

export type Wgs84Coordinates = {
  /**
   * Latitude (northing)
   * For example: 56.8787
   */
  latitude: number;
  /**
   * Longitude (easting)
   * For example: 14.7416
   */
  longitude: number;
  /**
   * Height as meters above sea level.
   * (Not ellipsiodal height)
   */
  height: number;
};

/**
 * Mathematical precision threshold.
 * When calculating if something is zero or not sometimes the result is not consistent due
 * to rounding errors. If the value is smaller than the precision threshold we will count it as
 * zero and vice versa.
 */
export const mathematicalPrecision = 0.00001;

export const getQuaternionBetweenVectors = (v1: Vector3, v2: Vector3) => {
  const { x, y, z } = v1.clone().cross(v2);

  const hypothenuse = Math.sqrt(Math.pow(v1.length(), 2) * Math.pow(v2.length(), 2));
  const w = hypothenuse + v1.dot(v2);

  return new Quaternion(x, y, z, w).normalize();
};

/**
 * Converts degrees to radians.
 */
export const degreesToRadians = (degrees: number) => {
  return ((degrees % 360) * Math.PI) / 180;
};

/**
 * Finds the closest point on a line to given target point given an origin and directional vector.
 */
export const findClosestPointOnRay = (origin: Vector3, dir: Vector3, point: Vector3) => {
  const lhs = point.clone().sub(origin);
  const dot = lhs.dot(dir);
  return origin.clone().add(dir.clone().multiplyScalar(dot));
};

export const toVector3 = (point: Point2d, height?: number): Vector3 => {
  return new Vector3(point.x, point.y, height ?? 0);
};

export const toVector2 = (point: Point2d): Vector2 => {
  return new Vector2(point.x, point.y);
};

/**
 * Converts NEH to XYZ
 * easting -> x
 * northing -> y
 * height -> z
 */
export const coordinateToPoint3d = (point: Coordinates): Point3d => {
  return {
    x: point.easting,
    y: point.northing,
    z: point.height,
  };
};

/**
 * Returns a unit vector that is perpendicular to the given vector.
 */
export function getPerpendicularVector(direction: Vector3) {
  const nonParallel =
    direction.x === 0 && direction.y === 0 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
  const perpendicular = direction.clone().cross(nonParallel);
  perpendicular.normalize();
  return perpendicular;
}

/**
 * Gets the perpendicular vector in the direction of rotationTarget vector
 */
export function getPerpendicularVectorInDirection(vector: Vector3, rotationTarget: Vector3) {
  // Calculate the cross product of v1 and v2
  const planeNormal = vector.clone().cross(rotationTarget).normalize();

  // Check the direction of the resulting vector
  if (rotationTarget.dot(planeNormal) < 0) {
    // If the dot product is negative, negate the resulting vector
    planeNormal.negate();
  }

  planeNormal.multiplyScalar(rotationTarget.length());
  const perpendicular = vector.clone().cross(planeNormal).normalize();

  // Check the direction of the resulting vector
  if (rotationTarget.dot(perpendicular) < 0) {
    // If the dot product is negative, negate the resulting vector
    perpendicular.negate();
  }

  return perpendicular;
}

/**
 * Clamps a value between min and max value
 */
export function clamp(value: number, min: number, max: number) {
  return value < min ? min : value > max ? max : value;
}

/**
 * Finds the closest point on a line to given target point given an origin and directional vector.
 */
export const findClosestPointOnLine = (start: Vector3, end: Vector3, point: Vector3) => {
  const v = end.clone().sub(start);
  const dir = v.clone().normalize();
  const closestPoint = findClosestPointOnRay(start, dir, point);

  return {
    point: closestPoint,
    // If the point is between the start and the end point then the distance can not be longer
    // than the total magnitude of the original vector
    onLine:
      closestPoint.distanceTo(start) <= v.length() && closestPoint.distanceTo(end) <= v.length(),
  };
};

/**
 * Takes in an origin and a directional vector and finds a point on the line where z equals
 * the desired height, if the line is horizontal undefeind will be returned.
 */
export const findPointOnLineFromHeight = (
  origin: Vector3,
  dir: Vector3,
  height: number,
): Vector3 | undefined => {
  if (dir.z === 0) {
    return undefined;
  }

  const { x: x0, y: y0, z: z0 } = origin;
  const { x: dirX, y: dirY, z: dirZ } = dir;

  const xRes = ((height - z0) * dirX) / dirZ + x0;
  const yRes = ((height - z0) * dirY) / dirZ + y0;

  return new Vector3(xRes, yRes, height);
};

export const getPolygonArea = (vertices: Point2d[]) => {
  return (
    Math.abs(
      vertices.reduce((prev, cur, i) => {
        const p1 = vertices[i];
        const p2 = vertices[(i + 1) % vertices.length];
        return prev + (p1.x - p2.x) * (p1.y + p2.y);
      }, 0),
    ) * 0.5
  );
};

/**
 * Gets the flattened distance squared between the two vertices.
 * This is useful for comparing the position of two Vector3 objects
 * in 2D space without needing to clone them.
 *
 * Note that this function just returns the relative distance value
 * between the points and not the actual distance.
 */
function flatDistanceSquared(v1: Point2d, v2: Point2d) {
  return (v1.x - v2.x) ** 2 + (v1.y - v2.y) ** 2;
}

export function flatDistance(v1: Point2d, v2: Point2d) {
  return Math.sqrt(flatDistanceSquared(v1, v2));
}

/**
 * Interpolates a value.
 * @param fraction A value between 0 and 1.
 */
export function interpolate(a: number, b: number, fraction: number) {
  return a * (1 - fraction) + b * fraction;
}

export const getPolyLineLength = (vertices: Vector3[], isPolygon = false) => {
  return Math.abs(
    vertices.reduce((prev, cur, i) => {
      if (i === vertices.length - 1 && !isPolygon) {
        return prev;
      }

      const p1 = vertices[i];
      const p2 = vertices[(i + 1) % vertices.length];
      return prev + p1.distanceTo(p2);
    }, 0),
  );
};

export const isPolygonClockwise = (vertices: Point2d[]) => {
  return (
    vertices.reduce((prev, cur, i) => {
      const p1 = vertices[i];
      const p2 = vertices[(i + 1) % vertices.length];
      return prev + (p1.x - p2.x) * (p1.y + p2.y);
    }, 0) > 0
  );
};

/**
 * Checks that the vector does not contain any NaN values or values with unsafe range.
 */
export const isValidVector3 = (v?: Vector3) =>
  v && isValidNumber(v.x) && isValidNumber(v.y) && isValidNumber(v.z);

/**
 * Converts radians to degrees.
 */
export const radiansToDegrees = (radians: number) => {
  return ((radians / Math.PI) * 180) % 360;
};

/**
 * @description Calculate the distance between points parameter
 */
export const calcPointDistances = <T extends { distanceTo: (arg0: T) => number }>(
  points: T[],
  loop = false,
) => {
  if (!points || points.length < 2) {
    return 0;
  }
  let distance = 0;
  for (let i = 0; i < points.length - 1; i++) {
    distance += Math.abs(points[i].distanceTo(points[i + 1]));
  }
  distance += loop ? Math.abs(points[0].distanceTo(points[points.length - 1])) : 0;
  return distance;
};

/**
 * @description Calculate the area in polygon
 */
export const calculateArea = (
  points: number[] | Float32Array,
  triangles: number[] | Uint32Array,
  planar: boolean,
): number => {
  let area = 0;

  let triangle: Vector3[] = [];
  triangles.forEach((tri, idx) => {
    //Every third in this loop makes a triangle and adds to the area.
    triangle.push(
      new THREE.Vector3(points[tri * 3], points[tri * 3 + 1], planar ? 0.0 : points[tri * 3 + 2]),
    );
    if (idx % 3 === 2) {
      area += new THREE.Triangle(triangle[0], triangle[1], triangle[2]).getArea();
      triangle = [];
    }
  });

  return area;
};

/**
 * @description Calculate the center point of supplied points
 */
export const calcMidPoint = (points: Vector3[]): Vector3 => {
  const minPoint = new THREE.Vector3(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER,
  );
  const maxPoint = new THREE.Vector3(
    Number.MIN_SAFE_INTEGER,
    Number.MIN_SAFE_INTEGER,
    Number.MIN_SAFE_INTEGER,
  );
  points.forEach((pos) => {
    minPoint.x = Math.min(minPoint.x, pos.x);
    minPoint.y = Math.min(minPoint.y, pos.y);
    minPoint.z = Math.min(minPoint.z, pos.z);
    maxPoint.x = Math.max(maxPoint.x, pos.x);
    maxPoint.y = Math.max(maxPoint.y, pos.y);
    maxPoint.z = Math.max(maxPoint.z, pos.z);
  });

  return new THREE.Vector3(
    minPoint.x + (maxPoint.x - minPoint.x) / 2,
    minPoint.y + (maxPoint.y - minPoint.y) / 2,
    minPoint.z + (maxPoint.z - minPoint.z) / 2,
  );
};

/**
 * Calculates the intersectio of a THREE.Ray and a plane using point and normal
 */
export const rayPlaneIntersection = (
  raycaster: Raycaster,
  planeNormal: Vector3,
  pointInPlane: Vector3,
) => {
  const normal = planeNormal.normalize();
  const denom = normal.dot(raycaster.ray.direction);
  if (Math.abs(denom) > mathematicalPrecision) {
    // your favorite epsilon
    const t = pointInPlane.clone().sub(raycaster.ray.origin).dot(normal) / denom;
    if (t >= 0) {
      return raycaster.ray.origin.clone().add(raycaster.ray.direction.clone().multiplyScalar(t));
    } // you might want to allow an epsilon here too
  }
  return undefined;
};

interface IntersectionLine {
  start: Vector3;
  end: Vector3;
}

/**
 * Checks if the direction of vector a and vector b are parallel to eachother
 */
export const areVectorsParallel = (a: Vector3, b: Vector3) => {
  return a.clone().normalize().cross(b.clone().normalize()).length() < mathematicalPrecision;
};

/**
 * Checks if the direction of vector a and vector b are parallel to eachother
 */
export const areVectorsParallel2d = (a: Vector3, b: Vector3) => {
  return (
    a.clone().setZ(0).normalize().cross(b.clone().setZ(0).normalize()).length() <
    mathematicalPrecision
  );
};

/**
 * Gets the normal of a face.
 */
export const getFaceNormal = (f1: Vector3, f2: Vector3, f3: Vector3) => {
  const a = f3.clone().sub(f1);
  const b = f3.clone().sub(f2);
  return a.cross(b).normalize();
};

/**
 * Checks if the point is contained within the polygon in a 2d space.
 */
export const polygonContainsPoint2d = (point: Point2d, polygon: Point2d[]) => {
  return pointInPolygon(
    [point.x, point.y],
    polygon.map((p) => [p.x, p.y]),
  );
};

export const checkLine2dIntersection = (line1: IntersectionLine, line2: IntersectionLine) => {
  // if the lines intersect, the result contains the x and y of the intersection (treating the lines as infinite) and booleans for whether line segment 1 or line segment 2 contain the point
  const result = {
    x: 0,
    y: 0,
    zLine1: 0,
    zLine2: 0,
    onLine1: false,
    onLine2: false,
  };

  const denominator =
    (line2.end.y - line2.start.y) * (line1.end.x - line1.start.x) -
    (line2.end.x - line2.start.x) * (line1.end.y - line1.start.y);
  if (denominator === 0) {
    return result;
  }
  const startDiffY = line1.start.y - line2.start.y;
  const startDiffX = line1.start.x - line2.start.x;
  const numerator1 =
    (line2.end.x - line2.start.x) * startDiffY - (line2.end.y - line2.start.y) * startDiffX;
  const numerator2 =
    (line1.end.x - line1.start.x) * startDiffY - (line1.end.y - line1.start.y) * startDiffX;

  const a = numerator1 / denominator;
  const b = numerator2 / denominator;

  // if we cast these lines infinitely in both directions, they intersect here:
  result.x = line1.start.x + a * (line1.end.x - line1.start.x);
  result.y = line1.start.y + a * (line1.end.y - line1.start.y);
  result.zLine1 = line1.start.z + a * (line1.end.z - line1.start.z);
  result.zLine2 = line2.start.z + b * (line2.end.z - line2.start.z);

  // if line1 is a segment and line2 is infinite, they intersect if:
  if (a > 0 && a < 1) {
    result.onLine1 = true;
  }
  // if line2 is a segment and line1 is infinite, they intersect if:
  if (b > 0 && b < 1) {
    result.onLine2 = true;
  }
  // if line1 and line2 are segments, they intersect if both of the above are true
  return result;
};
